
import Notification from '@/components/helper/Notification.vue'
import AuthImage from '@/components/image/AuthImage.vue'
import JigTextField from '@/components/input/JigTextField.vue'
import ImageUploader from '@/components/modals/ImageUploader.vue'
import PromptYesNo from '@/components/modals/PromptYesNo.vue'
import { SubscriptionConst, TenantConst } from '@/constants'
import SubscriptionCancelation from '@/mixin/SubscriptionCancelation'
import { BillingSource, BillingStatus, InvoiceStatus, PaymentIntentStatus, SubscriptionStatus } from '@/modules/subscription/types'
import { Permissions } from '@/security/permissions'
import { PermissionActions } from '@/store/constants'
import { FlexFeatureField, Tenant, TenantSetupJob, TenantSubscription } from '@/store/modules/app/types'
import { DynaconfConfig, Namespace, PricignListItem, PricingTier, UploadTenantLogoRequest } from '@/store/types'
import { AppHelper } from '@/utils/app-helper'
import { stripHexAlphaChannel } from '@/utils/color-helpers'
import { DateFormatter } from '@/utils/date-formatter'
import { ValidationRules } from '@/utils/input-validation'
import { ColorKey, TenantHelpers } from '@/utils/tenant-helpers'
import { ValidationRulesType } from '@/utils/type'
import IconLockSolid from '@svg/IconLockSolid.svg'
import { Component, Mixins, Prop, Vue, Watch } from 'vue-property-decorator'
import { Action, Getter, Mutation, State } from 'vuex-class'

declare var consoleLog: any

@Component({
  components: {
    Notification,
    AuthImage,
    JigTextField,
    ImageUploader,
    PromptYesNo,
    IconLockSolid,
  },
})
export default class TenantBrandingTab extends Mixins(SubscriptionCancelation) {
  @Prop({ required: true }) editedTenant!: Tenant
  @Prop({ type: Boolean, default: false }) isSuperUser!: boolean
  @Prop({ type: Boolean, default: false }) lacksTenantManagePermission!: boolean
  @Prop({ required: true }) nameRules!: any
  @Prop({ required: true }) urlRules!: any
  @Prop({ required: true }) defaultColor1Hex!: string

  @State('apiError', { namespace: Namespace.Utils })
  public apiError!: string

  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant

  @State('dynaconfConfig', { namespace: Namespace.App })
  public dynaconfConfig!: DynaconfConfig

  @State('dynaconfPricingListNames', { namespace: Namespace.App })
  private dynaconfPricingListNamesState!: SubscriptionConst.Subscription[]

  @State('tenantSetupJob', { namespace: Namespace.App })
  public tenantSetupJob!: TenantSetupJob

  @Mutation('updateMyTenant', { namespace: Namespace.App })
  public updateMyTenantState: any
  @Mutation('updateTenant', { namespace: Namespace.App })
  public updateTenant: any

  @Action('loadMyTenant', { namespace: Namespace.App })
  public loadMyTenant: any
  @Action('loadMyTenantLogo', { namespace: Namespace.App })
  public loadMyTenantLogo: any
  @Action('uploadTenantLogoImage', { namespace: Namespace.App })
  public uploadTenantLogoImage: any
  @Action('updateMyTenant', { namespace: Namespace.App })
  public updateMyTenantAction: any
  @Action('updateMyTenantsWithNewTenant', { namespace: Namespace.App })
  public updateMyTenantsWithNewTenant: any
  @Action('forceRerenderCoreView', { namespace: Namespace.App })
  public forceRerenderCoreView: any
  @Action('getTenantSetupJob', { namespace: Namespace.App })
  public getTenantSetupJob: any
  @Action('completeTenantSetupJob', { namespace: Namespace.App })
  public completeTenantSetupJob: any

  public $refs!: Vue['$refs'] & {
    logoImageUploader: ImageUploader
    savePromptYesNo: PromptYesNo
    cancelSubscriptionPromptYesNo: PromptYesNo
  }

  private readonly saveChangesSuccessMsg = 'Team changes saved'

  private formIsValid: boolean = false
  private isSaving: boolean = false

  private isUserBillingContact: boolean = false
  private isAppleBillingSource: boolean = false
  private isStripeBillingSource: boolean = false
  private isNoneBillingSource: boolean = false
  private tokenBillingStatus!: string

  private subscriptionStatusColor: string = ''
  private subscriptionInvoiceStatusColor: string = ''
  private subscriptionInvoicePaymentIntentStatusColor: string = ''
  private subscriptionShowContactUs: boolean = false
  private subscriptionStatusLabels = new Map([
    [SubscriptionStatus.Active, 'Active'],
    [SubscriptionStatus.Trialing, 'Trialing'],
    [SubscriptionStatus.IncompleteExpired, 'Requires Attention'],
    [SubscriptionStatus.Incomplete, 'Requires Attention'],
    [SubscriptionStatus.PastDue, 'Requires Attention'],
    [SubscriptionStatus.Unpaid, 'Requires Attention'],
    [SubscriptionStatus.Canceled, 'Canceled'],
    [BillingStatus.Paid, 'Paid'],
    [BillingStatus.Trial, 'Trialing'],
    [BillingStatus.BillingIssuesDetected, 'Requires Attention'],
    [BillingStatus.Expired, 'Expired'],
    [BillingStatus.UserUnsubscribed, 'User Unsubscribed'],
    [BillingStatus.NeverSubscribed, 'Not Found'],
    [BillingStatus.NewUnknownStatus, 'Internal Error'],
  ])

  private dynaconfPricingListNames: SubscriptionConst.Subscription[] = []
  private isLogoUploaderInitialized: boolean = false

  protected mounted() {
    this.$nextTick(() => {
      this.ensureLogoUploaderInitialized()
    })
  }

  private getLogoImageUploaderRef(): ImageUploader | undefined {
    const uploaderRef = this.$refs.logoImageUploader as unknown
    if (Array.isArray(uploaderRef)) {
      return uploaderRef[0] as ImageUploader | undefined
    }
    return uploaderRef as ImageUploader | undefined
  }

  private findLogoUploaderFromChildren(): ImageUploader | undefined {
    // In lazy-mounted tab content, refs can be briefly unavailable while vnode patches.
    // Fall back to the direct child that exposes the uploader API so the button still opens the dialog.
    const uploader = this.$children.find(
      (candidate: any) =>
        candidate != null &&
        typeof candidate.Show === 'function' &&
        typeof candidate.Hide === 'function' &&
        typeof candidate.Init === 'function'
    )

    return uploader as ImageUploader | undefined
  }

  private async ensureLogoUploaderInitialized(maxAttempts: number = 6): Promise<ImageUploader | undefined> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let uploader = this.getLogoImageUploaderRef()
      if (uploader == null) {
        uploader = this.findLogoUploaderFromChildren()
      }
      if (uploader != null) {
        // Tabs can mount lazily, so this ref may not be ready during first render ticks.
        if (!this.isLogoUploaderInitialized) {
          uploader.Init(ValidationRules.PngImageUpload)
          this.isLogoUploaderInitialized = true
        }
        return uploader
      }

      await this.$nextTick()
      await this.waitFor(80)
    }

    return
  }

  private waitFor(milliseconds: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds)
    })
  }

  // classic pricing set: set_2020_01
  private get isClassicPricingSet() {
    return this.editedTenant.Subscription != null && !this.dynaconfPricingListNames.includes(this.editedTenant.Subscription.PricingSet)
  }

  private get subscriptionStatusDisplay() {
    if (this.isAppleBillingSource) {
      return this.subscriptionStatusLabels.get(this.tokenBillingStatus as string)
    } else {
      return this.subscriptionStatusLabels.get(this.subscription.status as string)
    }
  }

  private get isSubscriptionNotActiveButHasInvoice() {
    return this.subscription.status !== SubscriptionStatus.Active && this.subscription.latest_invoice
  }

  private get isSubscriptionPaymentIntentRequiresAction() {
    if (this.subscription.latest_invoice.payment_intent) {
      return this.subscription.latest_invoice.payment_intent.status === PaymentIntentStatus.RequiresAction
    } else {
      return false
    }
  }

  private get isSubscriptionTrialling() {
    return this.subscription.status === SubscriptionStatus.Trialing
  }

  private get trialEndDisplay() {
    // trial_end is a unix timestamp, in seconds.
    return DateFormatter.formatSubscriptionDate(this.subscription.trial_end)
  }

  private get allowCustombranding(): boolean {
    return this.editedTenant.Subscription.AllowCustomBranding
  }

  private get hasCustomLogo(): boolean {
    return this.editedTenant.Logourl != null && this.editedTenant.Logourl.length > 0
  }

  private get logoImageUrl(): string {
    // Image endpoint is tenant-scoped via x-tenant-id header (handled by AuthImage).
    // Keep Logourl in the query so src changes when logo file key changes and refreshes preview.
    return `${process.env.VUE_APP_API_DOMAIN}/tenants/v1/images/v1?logo=${encodeURIComponent(this.editedTenant.Logourl)}`
  }

  // User can update branding (team settings) when:
  // 1. user is a Super User (JigStaff only)
  // 2. AllowCustomBranding is true and user has team manage permission (Team Manager / Team Admin)
  private get customBrandingDisabled(): boolean {
    if (!this.allowCustombranding && !this.isSuperUser) {
      return true
    }

    if (this.lacksTenantManagePermission) {
      return true
    }

    const uploader = this.getLogoImageUploaderRef()
    return this.isSaving || (uploader != null && uploader.IsShowingDialog)
  }

  // Disable the team field and active/inactive switcher when it's JigSpaceStaff tenant.
  private get isTenantJigSpaceStaff(): boolean {
    return TenantHelpers.IsTenantJigSpaceStaff(this.editedTenant)
  }

  private get tenantNotes(): string {
    return this.allowCustombranding
      ? 'People viewing your Jigs will go to this link if they click on your logo.'
      : 'Upgrade your plan to white label the JigSpace viewers.'
  }

  public async loadSubscription() {
    this.IsLoadingSubscription = true
    this.isUserBillingContact = false
    this.isAppleBillingSource = false
    this.isStripeBillingSource = false
    this.isNoneBillingSource = false

    const tenantTokens = this.$auth0.tenants
    const thisTenantToken = TenantHelpers.GetTenantTokenByTenantID(tenantTokens, this.editedTenant.ID)
    if (thisTenantToken === undefined) {
      this.IsLoadingSubscription = false
      return
    }

    this.isUserBillingContact = Permissions.TokenHasPermissionAction(
      thisTenantToken,
      Permissions.PermTenant,
      PermissionActions.BillingContact
    )
    this.isAppleBillingSource = thisTenantToken.billing_source === BillingSource.Apple
    this.isStripeBillingSource = thisTenantToken.billing_source === BillingSource.Stripe
    this.isNoneBillingSource = thisTenantToken.billing_source === BillingSource.None

    if (this.isUserBillingContact) {
      if (this.isAppleBillingSource) {
        this.tokenBillingStatus = thisTenantToken.billing_status
        if (this.tokenBillingStatus === BillingStatus.Paid || this.tokenBillingStatus === BillingStatus.Trial) {
          this.subscriptionStatusColor = 'green'
        } else {
          this.subscriptionStatusColor = 'red'
        }
      } else if (this.isStripeBillingSource) {
        if (
          await this.getSubscriptionWithInvoice({
            customerEmail: this.$auth0.user.email,
            tenantId: this.editedTenant.ID,
          })
        ) {
          let logMsg = `Subscription Status = ${this.subscription.status} `
          if (this.subscription.status === SubscriptionStatus.Active || this.subscription.status === SubscriptionStatus.Trialing) {
            this.subscriptionStatusColor = 'green'
          } else {
            this.subscriptionStatusColor = 'red'
            this.subscriptionShowContactUs =
              this.subscription.status === SubscriptionStatus.IncompleteExpired ||
              this.subscription.status === SubscriptionStatus.Canceled ||
              this.subscription.status === SubscriptionStatus.Unpaid
          }
          if (this.subscription.latest_invoice) {
            logMsg += `Latest Invoice Status = ${this.subscription.latest_invoice.status} `
            if (this.subscription.latest_invoice.status === InvoiceStatus.Paid) {
              this.subscriptionInvoiceStatusColor = 'green'
            } else {
              this.subscriptionInvoiceStatusColor = 'red'
            }
          }
          if (this.subscription.latest_invoice && this.subscription.latest_invoice.payment_intent) {
            logMsg += `Payment Intent Status = ${this.subscription.latest_invoice.payment_intent.status} `
            if (this.subscription.latest_invoice.payment_intent.status === PaymentIntentStatus.Succeeded) {
              this.subscriptionInvoicePaymentIntentStatusColor = 'green'
            } else {
              this.subscriptionInvoicePaymentIntentStatusColor = 'red'
            }
          }
          consoleLog(logMsg)
        }
        if (
          this.subscription.status === SubscriptionStatus.Active &&
          (await this.getTenantSetupJob({ userEmail: this.$auth0.user.email })) &&
          this.tenantSetupJob.status !== '' &&
          this.tenantSetupJob.createdTenantId === this.editedTenant.ID
        ) {
          if (await this.completeTenantSetupJob()) {
            await this.loadSubscription()
          }
        }
      }
    } else {
      consoleLog(`No subscription available because user is not a billing contact. Billing Source is ${thisTenantToken.billing_source}`)
    }
    this.IsLoadingSubscription = false
  }

  public updateDynaconfPricingListNames(names: SubscriptionConst.Subscription[]) {
    this.dynaconfPricingListNames = names
  }

  private async openLogoUploadDialog(tenant: any) {
    const uploader = await this.ensureLogoUploaderInitialized()
    if (uploader == null) {
      return
    }

    uploader.Show(
      null,
      (file: File): Promise<boolean> =>
        this.uploadTenantLogoImage({
          TenantId: this.editedTenant.ID,
          Image: file,
        } as UploadTenantLogoRequest),
      (): any => this.onPostLogoUpload()
    )
  }

  private async onPostLogoUpload() {
    const uploader = this.getLogoImageUploaderRef()
    if (uploader != null) {
      uploader.Hide()
    }

    await this.loadMyTenant({ TenantId: this.editedTenant.ID })
    const updatedTenant = AppHelper.deepCopyNestedJsonSafeObject(this.myTenant)

    updatedTenant.LogoUpdated = true
    this.updateMyTenantState(updatedTenant)
    if (this.isSuperUser) {
      this.updateTenant(updatedTenant)
      this.updateMyTenantsWithNewTenant(updatedTenant)
    }

    // Does this tenant have an uploaded logo to download?
    if (updatedTenant.Logourl !== '') {
      await this.loadMyTenantLogo(updatedTenant)
    }

    // Trigger core view refresh so global tenant logo consumers update immediately.
    this.forceRerenderCoreView(null)

    this.$emit('saved')
  }

  private showSaveTenantPrompt(t: Tenant) {
    this.$refs.savePromptYesNo.ShowPrompt()
  }

  private async onSavePromptSuccess(updatedTenant: Tenant) {
    this.saveChanges(updatedTenant)
  }

  private async saveChanges(updatedTenant: Tenant) {
    if (this.customBrandingDisabled) {
      return
    }

    this.isSaving = true

    const tenantCopy = AppHelper.deepCopyNestedJsonSafeObject(updatedTenant)

    tenantCopy.ColorHex1 = stripHexAlphaChannel(tenantCopy.ColorHex1)
    tenantCopy.ColorHex2 = stripHexAlphaChannel(tenantCopy.ColorHex2)

    // Append url protocol if there isn't one.
    if (tenantCopy.JigFinishedCustomLink && !tenantCopy.JigFinishedCustomLink.includes('://')) {
      tenantCopy.JigFinishedCustomLink = `http://${tenantCopy.JigFinishedCustomLink}`
    }

    await this.updateMyTenantAction(tenantCopy)

    if (this.apiError === undefined || this.apiError.length === 0) {
      this.$emit('snackbar', this.saveChangesSuccessMsg)
      await this.loadMyTenant({ TenantId: this.editedTenant.ID })

      if (this.isSuperUser) {
        this.updateMyTenantsWithNewTenant(AppHelper.deepCopyNestedJsonSafeObject(this.myTenant))
      }

      this.$emit('saved')
    }

    this.isSaving = false
  }

  private resetColorToDefault(tenant: Tenant, key: ColorKey) {
    if (key === 'Color1') {
      tenant.ColorHex1 = TenantHelpers.TenantDefaultColor1
    } else if (key === 'Color2') {
      tenant.ColorHex2 = TenantHelpers.TenantDefaultColor2
    }
  }

  private showCancelSubscriptionPrompt() {
    this.$refs.cancelSubscriptionPromptYesNo.ShowPrompt()
  }

  public clearLogoImageData() {
    const uploader = this.getLogoImageUploaderRef()
    if (uploader != null) {
      uploader.ClearImageData()
    }
  }
}
